/**
 * UpgradePrompt component for Cipher - Phase D.
 *
 * Shown when users try to access features that require a higher subscription tier.
 * Can be shown as inline prompt, compact badge, or full-screen modal.
 */
import { useState, useEffect, createContext, useContext, useCallback } from 'react'
import { Lock, Zap, TrendingUp, Crown, X, Check, Sparkles } from 'lucide-react'
import { Button, Card } from '../ui'
import { SubscriptionTier, TIER_NAMES, redirectToCheckout } from '../../api/billing'
import { usePermissions } from '../../hooks/usePermissions'
import './UpgradePrompt.css'

// Tier prices for display
const TIER_PRICES: Record<SubscriptionTier, number> = {
  free: 0,
  informed: 9,
  strategic: 29,
  apex_pro: 99
}

interface UpgradePromptProps {
  feature: string
  featureDescription?: string
  requiredTier?: SubscriptionTier
  onDismiss?: () => void
  compact?: boolean
}

const TIER_ICONS: Record<SubscriptionTier, React.ReactNode> = {
  free: null,
  informed: <Zap className="tier-icon" />,
  strategic: <TrendingUp className="tier-icon" />,
  apex_pro: <Crown className="tier-icon" />
}

// Updated feature descriptions for Phase D
const FEATURE_DESCRIPTIONS: Record<string, string> = {
  backtester: 'Test your trading strategies against historical data to see how they would have performed.',
  custom_strategies: 'Create custom trading strategies with your own rules and conditions.',
  score_breakdown: 'See the detailed breakdown of how Cipher Scores are calculated for each ticker.',
  ai_analysis: 'Get AI-powered analysis of stocks, earnings, and market conditions.',
  options_intelligence: 'Access advanced options flow analysis and unusual activity alerts.',
  news_intelligence: 'Get full access to news impact analysis across all sources.',
  causal_network: 'Visualize how world events causally impact your portfolio.',
  advanced_alerts: 'Set up 14+ types of advanced alerts including technical and sentiment triggers.',
  real_time_scores: 'Get real-time Cipher Score updates with detailed drill-down analysis.',
  api_access: 'Build your own tools and integrations using the Cipher API.',
  priority_support: 'Get priority support from our team with < 2 hour response times.',
  watchlist_unlimited: 'Add unlimited tickers to your watchlist.',
  portfolio_multiple: 'Track multiple portfolios with unlimited positions.'
}

// Pro tier features for the modal
const PRO_FEATURES = [
  'Unlimited watchlist',
  'AI analysis (50 queries/day)',
  'Full backtesting engine',
  'Real-time Cipher Scores',
  'Advanced alerts (14 types)',
  'Options intelligence',
  'Causal network visualization'
]

// Elite tier features
const ELITE_FEATURES = [
  'Everything in Pro',
  'Unlimited AI analysis',
  'API access',
  'Priority support (< 2hr)',
  'Early access features'
]

export function UpgradePrompt({
  feature,
  featureDescription,
  requiredTier,
  onDismiss,
  compact = false
}: UpgradePromptProps) {
  const { requiresTier } = usePermissions()
  const [isLoading, setIsLoading] = useState(false)

  const targetTier = requiredTier || requiresTier(feature)
  const tierName = TIER_NAMES[targetTier]
  const price = TIER_PRICES[targetTier]
  const description = featureDescription || FEATURE_DESCRIPTIONS[feature] || ''

  const handleUpgrade = async () => {
    setIsLoading(true)
    try {
      await redirectToCheckout(targetTier)
    } catch (error) {
      console.error('Failed to start checkout:', error)
      setIsLoading(false)
    }
  }

  if (compact) {
    return (
      <div className="upgrade-prompt-compact">
        <Lock className="lock-icon" />
        <span>
          <strong>{feature}</strong> requires {tierName}
        </span>
        <Button
          variant="primary"
          size="sm"
          onClick={handleUpgrade}
          loading={isLoading}
        >
          Upgrade
        </Button>
      </div>
    )
  }

  return (
    <Card className="upgrade-prompt">
      {onDismiss && (
        <button className="dismiss-button" onClick={onDismiss} aria-label="Dismiss">
          <X size={20} />
        </button>
      )}

      <div className="upgrade-prompt-content">
        <div className="upgrade-icon-wrapper">
          <Lock className="upgrade-lock-icon" />
        </div>

        <h3 className="upgrade-title">Upgrade to Unlock</h3>

        <div className="feature-highlight">
          {TIER_ICONS[targetTier]}
          <span className="feature-name">{formatFeatureName(feature)}</span>
        </div>

        {description && (
          <p className="feature-description">{description}</p>
        )}

        <div className="tier-badge">
          {TIER_ICONS[targetTier]}
          <span>Available on {tierName} and above</span>
        </div>

        <div className="upgrade-pricing">
          <span className="price">${price}</span>
          <span className="period">/month</span>
        </div>

        <div className="upgrade-actions">
          <Button
            variant="primary"
            size="lg"
            onClick={handleUpgrade}
            loading={isLoading}
            fullWidth
          >
            <Zap size={18} />
            Upgrade to {tierName}
          </Button>

          {onDismiss && (
            <button className="maybe-later" onClick={onDismiss}>
              Maybe later
            </button>
          )}
        </div>

        <p className="upgrade-note">
          Cancel anytime. You'll keep access until your billing period ends.
        </p>
      </div>
    </Card>
  )
}

/**
 * Format a feature key to a human-readable name
 */
function formatFeatureName(feature: string): string {
  return feature
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * FeatureGate component - wraps content and shows upgrade prompt if not accessible
 */
interface FeatureGateProps {
  feature: string
  children: React.ReactNode
  fallback?: React.ReactNode
}

export function FeatureGate({ feature, children, fallback }: FeatureGateProps) {
  const { can } = usePermissions()

  if (can(feature as any)) {
    return <>{children}</>
  }

  if (fallback) {
    return <>{fallback}</>
  }

  return <UpgradePrompt feature={feature} />
}

/**
 * LimitGate component - checks numeric limits
 */
interface LimitGateProps {
  limitName: string
  currentCount: number
  children: React.ReactNode
  onLimitReached?: () => void
}

export function LimitGate({ limitName, currentCount, children, onLimitReached }: LimitGateProps) {
  const { withinLimit, getLimit } = usePermissions()

  if (withinLimit(limitName as any, currentCount)) {
    return <>{children}</>
  }

  const limit = getLimit(limitName as any)

  return (
    <UpgradePrompt
      feature={limitName}
      featureDescription={`You've reached your limit of ${limit}. Upgrade to add more.`}
      onDismiss={onLimitReached}
    />
  )
}

export default UpgradePrompt


// ============================================
// UPGRADE MODAL - Full-screen modal version
// ============================================

interface UpgradeModalProps {
  isOpen: boolean
  onClose: () => void
  feature?: string
  targetTier?: SubscriptionTier
}

export function UpgradeModal({ isOpen, onClose, feature, targetTier = 'strategic' }: UpgradeModalProps) {
  const [isLoading, setIsLoading] = useState(false)

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleUpgrade = async () => {
    setIsLoading(true)
    try {
      await redirectToCheckout(targetTier)
    } catch (error) {
      console.error('Failed to start checkout:', error)
      setIsLoading(false)
    }
  }

  const isElite = targetTier === 'apex_pro'
  const features = isElite ? ELITE_FEATURES : PRO_FEATURES
  const price = isElite ? 99 : 29
  const tierName = isElite ? 'Elite' : 'Pro'

  return (
    <div className="upgrade-modal-overlay" onClick={onClose}>
      <div className="upgrade-modal" onClick={(e) => e.stopPropagation()}>
        <button className="upgrade-modal-close" onClick={onClose} aria-label="Close">
          <X size={24} />
        </button>

        <div className="upgrade-modal-content">
          <div className="upgrade-modal-icon">
            {isElite ? <Crown size={48} /> : <Sparkles size={48} />}
          </div>

          <h2 className="upgrade-modal-title">
            {feature ? 'Feature Requires Upgrade' : `Upgrade to Cipher ${tierName}`}
          </h2>

          {feature && (
            <p className="upgrade-modal-feature">
              <Lock size={16} />
              <strong>{formatFeatureName(feature)}</strong> requires Cipher {tierName}
            </p>
          )}

          <p className="upgrade-modal-message">
            {isElite
              ? 'Get unlimited AI, API access, and priority support.'
              : 'Unlock unlimited watchlist, AI analysis, backtesting, and more.'
            }
          </p>

          <div className="upgrade-modal-price">
            <span className="price-amount">${price}</span>
            <span className="price-period">/month</span>
          </div>

          <ul className="upgrade-modal-features">
            {features.map((f, i) => (
              <li key={i}>
                <Check size={16} className="check-icon" />
                {f}
              </li>
            ))}
          </ul>

          <div className="upgrade-modal-actions">
            <Button
              variant="primary"
              size="lg"
              onClick={handleUpgrade}
              loading={isLoading}
              fullWidth
            >
              <Zap size={18} />
              Upgrade Now
            </Button>

            <button className="upgrade-modal-later" onClick={onClose}>
              Maybe Later
            </button>
          </div>

          <p className="upgrade-modal-note">
            7-day free trial included. Cancel anytime.
          </p>
        </div>
      </div>
    </div>
  )
}


// ============================================
// UPGRADE MODAL CONTEXT - For showing from anywhere
// ============================================

interface UpgradeModalContextType {
  showUpgradeModal: (feature?: string, targetTier?: SubscriptionTier) => void
  hideUpgradeModal: () => void
}

const UpgradeModalContext = createContext<UpgradeModalContextType | null>(null)

export function UpgradeModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [feature, setFeature] = useState<string | undefined>()
  const [targetTier, setTargetTier] = useState<SubscriptionTier>('strategic')

  const showUpgradeModal = useCallback((feat?: string, tier?: SubscriptionTier) => {
    setFeature(feat)
    setTargetTier(tier || 'strategic')
    setIsOpen(true)
  }, [])

  const hideUpgradeModal = useCallback(() => {
    setIsOpen(false)
    setFeature(undefined)
  }, [])

  return (
    <UpgradeModalContext.Provider value={{ showUpgradeModal, hideUpgradeModal }}>
      {children}
      <UpgradeModal
        isOpen={isOpen}
        onClose={hideUpgradeModal}
        feature={feature}
        targetTier={targetTier}
      />
    </UpgradeModalContext.Provider>
  )
}

export function useUpgradeModal() {
  const context = useContext(UpgradeModalContext)
  if (!context) {
    throw new Error('useUpgradeModal must be used within UpgradeModalProvider')
  }
  return context
}


// ============================================
// PRO BADGE - Shows on locked sidebar items
// ============================================

interface ProBadgeProps {
  tier?: SubscriptionTier
  className?: string
}

export function ProBadge({ tier = 'strategic', className = '' }: ProBadgeProps) {
  const label = tier === 'apex_pro' ? 'ELITE' : 'PRO'
  return (
    <span className={`pro-badge pro-badge-${tier} ${className}`}>
      {label}
    </span>
  )
}
